
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { exportSubscriptions, exportSubscriptionServices } from '@/services/exportService';
import { useLanguage } from '@/hooks/useLanguage';

export type ExportType = 'subscriptions' | 'subscription-services';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exportType: ExportType;
  defaultEmail: string;
}

const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onOpenChange,
  exportType,
  defaultEmail,
}) => {
  const { translate } = useLanguage();
  const [email, setEmail] = useState(defaultEmail);
  const [isLoading, setIsLoading] = useState(false);

  const handleExport = async () => {
    if (!email || !email.includes('@')) {
      toast.error(translate('export.invalidEmail'));
      return;
    }

    setIsLoading(true);
    try {
      const result = exportType === 'subscriptions' 
        ? await exportSubscriptions(email)
        : await exportSubscriptionServices(email);

      if (result.success) {
        toast.success(result.message || translate('export.success'));
        onOpenChange(false);
      } else {
        toast.error(result.error || translate('export.error'));
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(translate('export.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const getTitle = () => {
    return exportType === 'subscriptions' 
      ? translate('export.subscriptions.title')
      : translate('export.subscriptionServices.title');
  };

  const getDescription = () => {
    return exportType === 'subscriptions'
      ? translate('export.subscriptions.description')
      : translate('export.subscriptionServices.description');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>
            {getDescription()}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="email" className="text-right">
              {translate('export.email')}
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="col-span-3"
              placeholder={translate('export.emailPlaceholder')}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {translate('common.cancel')}
          </Button>
          <Button onClick={handleExport} disabled={isLoading}>
            {isLoading ? translate('export.sending') : translate('export.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialog;
